import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import {
  DataUpdateRequestItem,
  getItemPreviewUrl,
  ItemStatus,
} from 'src/app/dashboard/interfaces/data-update-request.interface';
import { DataUpdateRequestService } from 'src/app/dashboard/services/data-update-request.service';

export type UploadState = 'idle' | 'uploading' | 'confirming' | 'error';

/**
 * El `fieldKey` de este item ya no está en la solicitud vigente: el
 * operador la re-emitió con otros campos, así que este documento ya no
 * se pide. No es reintentable — hay que recargar la vista.
 */
class SolicitudCambioError extends Error {
  constructor() {
    super('El fieldKey ya no pertenece a la solicitud vigente.');
    this.name = 'SolicitudCambioError';
  }
}

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ACCEPTED_MIME = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'application/pdf',
];

/**
 * Maneja el flujo de upload de un item de archivo:
 *   1) POST /file-upload-url → recibe URL pre-firmada + key
 *   2) PUT directo a S3 con el `Content-Type` exacto (vía fetch para
 *      evitar interceptors — el host es AWS, no Aguacate)
 *   3) POST /file-confirm → backend marca como SUBMITTED y dispara
 *      OCR/risk checks server-side
 *
 * Estado visual (segun `item.status` + `uploadState` local):
 *   - PENDING + idle  → drop zone vacío (CTA: subir)
 *   - * + uploading   → spinner + barra indeterminada
 *   - * + confirming  → spinner "confirmando"
 *   - SUBMITTED       → preview + chip "En revisión"
 *   - APPROVED        → preview + chip "Aprobado"
 *   - REJECTED        → preview + chip "Rechazado" + motivo + CTA "subir otra"
 *
 * Emite `fileConfirmed` tras paso 3 exitoso para que el padre refresque
 * el detalle y se vea el chip actualizado.
 */
@Component({
  selector: 'app-update-field-file',
  templateUrl: './update-field-file.component.html',
  styleUrls: ['./update-field-file.component.sass'],
})
export class UpdateFieldFileComponent {
  @Input() item!: DataUpdateRequestItem;
  @Input() requestId: string = '';
  @Input() label: string = '';
  @Input() disabled: boolean = false;

  /**
   * Emite el `itemId` del archivo recién confirmado (tras un
   * `file-confirm` exitoso). El padre lo usa para (a) refrescar el
   * detalle y (b) marcar el item como satisfecho localmente aunque el
   * `status` que devuelva el backend todavía no haya pasado a SUBMITTED
   * (el pipeline de OCR/verificación es asíncrono).
   */
  @Output() fileConfirmed = new EventEmitter<string>();

  /**
   * Input files ocultos disparados con `.click()` desde botones "reales".
   * Envolver el input en un `<label>` clickable resulta inconsistente
   * entre navegadores (a veces el label no propaga el click al input
   * cuando el input tiene `pointer-events: none`); dispararlo desde el
   * botón evita el problema.
   */
  @ViewChild('initialFileInput') initialFileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('retryFileInput') retryFileInput?: ElementRef<HTMLInputElement>;

  /**
   * Emite cada vez que cambia el `uploadState` local. El padre lo usa
   * para invalidar el botón "Enviar para revisión" mientras hay un
   * upload en curso o si quedó en error — no queremos que el cliente
   * envíe la solicitud con un archivo a medio subir.
   */
  @Output() uploadStateChange = new EventEmitter<UploadState>();

  private _uploadState: UploadState = 'idle';
  get uploadState(): UploadState {
    return this._uploadState;
  }
  set uploadState(next: UploadState) {
    if (this._uploadState === next) return;
    this._uploadState = next;
    this.uploadStateChange.emit(next);
  }

  errorMessage: string | null = null;

  /** Preview local del archivo recién elegido — base64 mientras se sube. */
  localPreview: string | null = null;

  /**
   * `true` cuando el modal de cámara está abierto. Lo controla el
   * método `openCamera()` y el propio componente `<app-document-camera-capture>`
   * lo cierra al confirmar/cancelar.
   */
  cameraOpen: boolean = false;

  constructor(private service: DataUpdateRequestService) {}

  /**
   * Etiqueta del modal según el `fieldKey` del item. La guía al usuario
   * sobre qué documento tiene que capturar.
   */
  get cameraTitle(): string {
    switch (this.item?.fieldKey) {
      case 'fotoIdfrontal':
        return 'Foto frontal del documento';
      case 'fotoIdPosterior':
        return 'Foto reverso del documento';
      case 'selfie':
        return 'Selfie del rostro';
      default:
        return this.label || 'Captura de documento';
    }
  }

  get cameraSubtitle(): string {
    if (this.item?.fieldKey === 'selfie') {
      return 'Mirá de frente a la cámara y encuadrá tu rostro dentro del óvalo.';
    }
    return 'Alineá el documento dentro del marco y evitá reflejos.';
  }

  /**
   * Aspect ratio del frame overlay. Para selfies usamos vertical
   * (portrait ~0.78, alto > ancho) que calza con el óvalo de rostro;
   * para cédulas y otros documentos, el ratio estándar (1.586).
   */
  get cameraFrameAspectRatio(): number {
    if (this.item?.fieldKey === 'selfie') return 0.78;
    return 1.586;
  }

  /**
   * Forma del frame overlay. Para selfie usamos el óvalo (frameShape
   * 'face'): las esquinas rectangulares hacían creer a los clientes
   * que tenían que fotografiar sólo el documento.
   */
  get cameraFrameShape(): 'document' | 'face' {
    return this.item?.fieldKey === 'selfie' ? 'face' : 'document';
  }

  /**
   * Cámara preferida al abrir el modal. Selfies con la frontal;
   * documentos con la trasera. El cliente puede alternar dentro del
   * modal con el botón "Cambiar cámara" si el dispositivo tiene más
   * de una.
   */
  get cameraPreferredFacing(): 'user' | 'environment' {
    return this.item?.fieldKey === 'selfie' ? 'user' : 'environment';
  }

  openCamera(): void {
    if (!this.canUpload) return;
    this.errorMessage = null;
    this.cameraOpen = true;
  }

  closeCamera(): void {
    this.cameraOpen = false;
  }

  /**
   * Handler del `(captured)` del componente de cámara. Recibe base64
   * JPEG procesado (recortado al frame, comprimido a < 4.5MB) y lo
   * mete al flujo de upload como si fuera un `File` cualquiera.
   *
   * Nota sobre naming: el nombre del `File` en JS NO afecta la key en
   * S3 — el backend define la key (`<pais>/<cognitoId>/<campo>.<ext>`
   * según §3.2 del plan) y el PUT usa esa key firmada, no el filename
   * del blob. Igual usamos un nombre trazable por consistencia en
   * logs/DevTools.
   */
  async onCameraCaptured(base64: string): Promise<void> {
    this.cameraOpen = false;
    const filename = this.buildFilenameFromField(this.item.fieldKey, 'jpg');
    const file = this.base64ToFile(base64, filename, 'image/jpeg');
    this.localPreview = base64;
    await this.upload(file);
  }

  onCameraError(message: string): void {
    this.cameraOpen = false;
    this.errorMessage = message;
    this.uploadState = 'error';
  }

  /**
   * Construye un nombre "seguro" para el File JS. Sanitiza el fieldKey
   * a `[a-zA-Z0-9_-]` para evitar cualquier char raro en logs; la
   * extensión sale del MIME (siempre `.jpg` en cámara).
   */
  private buildFilenameFromField(fieldKey: string, ext: string): string {
    const safe = (fieldKey || 'documento').replace(/[^a-zA-Z0-9_-]/g, '_');
    return `${safe}.${ext}`;
  }

  private base64ToFile(base64: string, filename: string, mime: string): File {
    const arr = base64.split(',');
    const bstr = atob(arr[1] || '');
    const u8 = new Uint8Array(bstr.length);
    for (let i = 0; i < bstr.length; i++) u8[i] = bstr.charCodeAt(i);
    return new File([u8], filename, { type: mime });
  }

  get acceptAttr(): string {
    return ACCEPTED_MIME.join(',');
  }

  get hasRemoteFile(): boolean {
    return !!this.remoteUrl;
  }

  /**
   * URL pre-firmada del archivo actual. El backend real la devuelve
   * como `previewUrl`; el shape histórico usaba `fileSignedUrl`.
   * `getItemPreviewUrl` cubre ambos.
   */
  get remoteUrl(): string | null {
    return getItemPreviewUrl(this.item);
  }

  get canUpload(): boolean {
    return (
      !this.disabled &&
      this.uploadState === 'idle' &&
      (this.item.status === 'PENDING' || this.item.status === 'REJECTED')
    );
  }

  get showPreviewArea(): boolean {
    return (
      !!this.localPreview ||
      this.hasRemoteFile ||
      this.item.status === 'APPROVED' ||
      this.item.status === 'SUBMITTED' ||
      this.item.status === 'REJECTED'
    );
  }

  statusLabel(status: ItemStatus): string {
    switch (status) {
      case 'PENDING':
        return 'Pendiente';
      case 'SUBMITTED':
        return 'En revisión';
      case 'APPROVED':
        return 'Aprobado';
      case 'REJECTED':
        return 'Rechazado';
      default:
        return status;
    }
  }

  statusColor(status: ItemStatus): string {
    switch (status) {
      case 'PENDING':
        return '#f59e0b';
      case 'SUBMITTED':
        return '#3b82f6';
      case 'APPROVED':
        return '#16a34a';
      case 'REJECTED':
        return '#dc2626';
      default:
        return '#6b7280';
    }
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    // Reset el input para que el mismo archivo se pueda re-seleccionar.
    input.value = '';

    this.errorMessage = null;

    // Validación cliente
    if (!ACCEPTED_MIME.includes(file.type)) {
      this.errorMessage = 'Formato no soportado. Subí JPG, PNG, WEBP o PDF.';
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      this.errorMessage = 'El archivo supera los 10 MB.';
      return;
    }

    this.localPreview = await this.readAsDataUrl(file);
    await this.upload(file);
  }

  private async upload(file: File): Promise<void> {
    this.uploadState = 'uploading';
    try {
      await this.uploadOnce(file);
      this.uploadState = 'idle';
      this.localPreview = null;
      this.fileConfirmed.emit(this.lastItemId);
    } catch (err: any) {
      // Id de item obsoleto (la solicitud fue re-emitida): refrescamos
      // resolviendo el id fresco por `fieldKey` y reintentamos UNA vez.
      if (this.service.isItemNoVigente(err)) {
        try {
          this.uploadState = 'uploading';
          await this.uploadOnce(file);
          this.uploadState = 'idle';
          this.localPreview = null;
          this.fileConfirmed.emit(this.lastItemId);
          return;
        } catch (err2: any) {
          this.setUploadError(err2);
          return;
        }
      }
      this.setUploadError(err);
    }
  }

  /** Id del item resuelto en el último intento (para emitir a confirmación). */
  private lastItemId = '';

  /**
   * Un intento completo de subida resolviendo el id FRESCO por `fieldKey`
   * (nunca cacheado): 1) `GET /me` → id vigente, 2) URL pre-firmada,
   * 3) PUT a S3, 4) file-confirm. Lanza si el `fieldKey` ya no está en la
   * solicitud (cambió lo solicitado).
   */
  private async uploadOnce(file: File): Promise<void> {
    // Paso 0: resolver requestId + itemId frescos por fieldKey.
    const fresh = await this.service.resolveFreshItem(this.item.fieldKey);
    if (!fresh) {
      // El fieldKey ya no está entre los items → la solicitud cambió.
      throw new SolicitudCambioError();
    }
    const { requestId, itemId } = fresh;
    this.lastItemId = itemId;

    // Paso 1: URL pre-firmada
    const { url, key } = await this.service.getUploadUrl(
      requestId,
      itemId,
      file.type,
    );

    // Paso 2: PUT directo a S3. fetch en lugar de HttpClient para evitar
    // que ApiRestInterceptor inyecte el Authorization header (S3 rechaza
    // requests con headers extras).
    const putRes = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });
    if (!putRes.ok) {
      throw new Error(`S3 upload falló con HTTP ${putRes.status}`);
    }

    // Paso 3: confirmar
    this.uploadState = 'confirming';
    await this.service.confirmUpload(requestId, itemId, key);
  }

  private setUploadError(err: any): void {
    this.uploadState = 'error';
    if (err instanceof SolicitudCambioError) {
      this.errorMessage =
        'La solicitud cambió y este documento ya no se requiere. Recargá la página para ver los documentos vigentes.';
      return;
    }
    this.errorMessage =
      err?.error?.message ||
      err?.message ||
      'No pudimos subir el archivo. Reintentá.';
  }

  retry(): void {
    this.uploadState = 'idle';
    this.errorMessage = null;
    this.localPreview = null;
  }

  /** Dispara el input file oculto del drop zone inicial. */
  triggerInitialFileInput(): void {
    if (!this.canUpload) return;
    this.initialFileInput?.nativeElement?.click();
  }

  /** Dispara el input file oculto del bloque de reemplazo. */
  triggerRetryFileInput(): void {
    if (!this.canUpload) return;
    this.retryFileInput?.nativeElement?.click();
  }

  /**
   * `true` solo cuando podemos rotar la imagen actual (hay una imagen
   * cargada, no es PDF y el item permite reemplazo). Los PDFs no se
   * rotan client-side porque no los renderizamos como <img>.
   */
  get canRotate(): boolean {
    if (!this.canUpload) return false;
    if (!this.localPreview && !this.remoteUrl) return false;
    // Si conocemos el mime del archivo actual y es PDF, no rotamos.
    const mime = this.currentFileMime;
    if (mime === 'application/pdf') return false;
    return true;
  }

  /**
   * Mejor esfuerzo por detectar el mime del archivo actual: primero el
   * `contentType` que devuelve el backend, si no la extensión del
   * `fileKey`, si no la del `previewUrl`.
   */
  private get currentFileMime(): string | null {
    const contentType = (this.item as any)?.contentType;
    if (typeof contentType === 'string' && contentType.length) return contentType;
    const source = this.item.fileKey || this.remoteUrl || '';
    const lower = source.toLowerCase().split('?')[0];
    if (lower.endsWith('.pdf')) return 'application/pdf';
    if (lower.endsWith('.png')) return 'image/png';
    if (lower.endsWith('.webp')) return 'image/webp';
    if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
    return null;
  }

  /**
   * Rota la imagen actual 90° en sentido horario (`cw`) o antihorario
   * (`ccw`) y re-sube el resultado usando el mismo flujo de upload.
   *
   * La rotación se hace en un `<canvas>` local: cargamos el source
   * (localPreview base64 o remoteUrl firmado), lo redibujamos rotado y
   * exportamos como JPEG. El nuevo `File` reemplaza al archivo actual
   * del item — el backend lo trata como un reemplazo cualquiera.
   *
   * Notas:
   *  - Requiere que el bucket S3 tenga CORS habilitado para GET desde
   *    el dominio de la wallet, ya que el canvas se "contaminaría" con
   *    una imagen cross-origin sin CORS y `toBlob()` fallaría.
   *  - JPEG en lugar de PNG: el flujo del backend (OCR / verificación
   *    facial) espera imágenes; JPEG es más liviano y ya perdimos
   *    calidad al comprimir en cámara — no tiene sentido re-encodear a
   *    PNG.
   */
  async rotate(direction: 'cw' | 'ccw'): Promise<void> {
    if (!this.canRotate) return;
    const source = this.localPreview || this.remoteUrl;
    if (!source) return;

    this.errorMessage = null;
    this.uploadState = 'uploading';

    try {
      const img = await this.loadImage(source);
      const canvas = document.createElement('canvas');
      // Al rotar 90°, ancho y alto se intercambian.
      canvas.width = img.height;
      canvas.height = img.width;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('No pudimos crear el canvas para rotar.');
      const angle = direction === 'cw' ? 90 : -90;
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((angle * Math.PI) / 180);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);

      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', 0.9),
      );
      if (!blob) throw new Error('No pudimos generar la imagen rotada.');

      const filename = this.buildFilenameFromField(this.item.fieldKey, 'jpg');
      const file = new File([blob], filename, { type: 'image/jpeg' });
      this.localPreview = await this.readAsDataUrl(file);
      // `upload()` resetea el uploadState a 'uploading' internamente —
      // ok, no hay estado que perder.
      await this.upload(file);
    } catch (err: any) {
      this.uploadState = 'error';
      this.errorMessage =
        err?.message ||
        'No pudimos rotar la imagen. Reintentá o subí una nueva.';
    }
  }

  /**
   * Carga una URL (base64 o remota) en un `HTMLImageElement`.
   * `crossOrigin='anonymous'` es necesario para que el canvas no quede
   * "tainted" cuando la imagen viene de S3 — sin esto `toBlob()` tira
   * SecurityError.
   */
  private loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      // Solo declaramos crossOrigin para URLs remotas; para data:URLs
      // no aplica y algunos browsers loguean warning.
      if (!src.startsWith('data:')) {
        img.crossOrigin = 'anonymous';
      }
      img.onload = () => resolve(img);
      img.onerror = () =>
        reject(new Error('No pudimos cargar la imagen para rotarla.'));
      img.src = src;
    });
  }

  private readAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }
}
